import React, { FC, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Input from "shared/Input/Input";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useForm } from "react-hook-form";
import { auth } from "../../firebase/config";
import { useAlert } from "../../@shared/hooks/useAlert";
import { useLoader } from "../../@shared/hooks/useLoader";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { useNavigate } from "react-router-dom";

export interface PageResetPasswordProps {
  className?: string;
}

interface IResetPasswordForm {
  password: string,
  repeatPassword: string
}

const PageResetPassword: FC<PageResetPasswordProps> = ({ className = "" }) => {
  const alert = useAlert();
  const loader = useLoader();
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm();
  const queryParams = new URLSearchParams(window.location.search);
  const oobCode = queryParams.get("oobCode");
  const handleSubmitForm = async (data: IResetPasswordForm) => {
    if (data.password.trim() !== data.repeatPassword.trim()) {
      alert.error("Passwords do not match");
      return;
    }

    try {
      loader.start();
      await confirmPasswordReset(auth, oobCode, data.password);
      alert.success("Password updated successfully");
      navigate("/login");
    } catch (error) {
      alert.error("Error updating password");
    } finally {
      loader.stop();
    }
  };

  useEffect(() => {
    verifyPasswordResetCode(auth, oobCode).catch(() => navigate("/"));
  }, []);

  return (
    <div className={`nc-forgot-password  ${className}`} data-nc-id="forgot-password">
      <Helmet>
        <title>Reset your password</title>
      </Helmet>
      <div className="container mb-24 lg:mb-32">
        <h2 className="my-20 flex items-center text-3xl leading-[115%] md:text-5xl md:leading-[115%] font-semibold text-neutral-900 dark:text-neutral-100 justify-center">
          Recover password
        </h2>
        <div className="max-w-md mx-auto space-y-6 ">
          {/* FORM */}
          <form className="grid grid-cols-1 gap-6" onSubmit={handleSubmit(handleSubmitForm)}>
            <label className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                New password
              </span>
              <Input
                type="password"
                className="mt-1"
                {...register("password", {
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters"
                  },
                  required: {
                    value: true,
                    message: "Password is required"
                  }
                })}
              />
              {errors && errors.password && (
                <span className="text-red-500 text-sm mt-1">
                  {errors.password.message.toString()}
                </span>
              )}
            </label>

            <label className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                Repeat password
              </span>
              <Input
                type="password"
                className="mt-1"
                {...register("repeatPassword")}
              />
            </label>
            <ButtonPrimary type="submit">Submit</ButtonPrimary>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PageResetPassword;
